import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import styled from 'styled-components';

import type { IGroupFinanceAgreement } from 'core/utils/groupMoney';

import * as S from '../../FakturowniaInvoices/FakturowniaInvoices.styled';
import * as L from '../../_detailShared/detail.styled';
import { formatMoneyCurrency } from '../../_detailShared/format';
import { GroupColors } from './groupColors';

interface IContributionsSummaryProps {
  /**
   * The group's agreements (each carrying its `contributions` schedule),
   * already fetched by the parent `FinancesTab` via
   * `useGroupFinanceContributions(group.id)`.
   *
   * Task 8.1 removed the inline `agreements` block from the slim
   * `GroupDetailQuery`, so this component no longer reads it off the group.
   * The agreements are passed
   * **down** from `FinancesTab` (which already runs the lazy contributions
   * query for the Money_Model) rather than this component running its own
   * query — that keeps the contract side a single query for the whole tab
   * instead of fetching the same payload twice.
   */
  agreements: IGroupFinanceAgreement[];
}

interface ICurrencyRow {
  currency: string;
  planned: number;
  paid: number;
  outstanding: number;
  overdueCount: number;
  upcomingCount: number;
}

/**
 * Per-currency Contributions summary for the GroupDetail FinancesTab.
 *
 * Source: walks `agreements[].contributions.edges[].node` of the active
 * agreements (a terminated agreement's schedule is closed by definition).
 * For each currency emits one row:
 *   - planned     — Σ `amountForeign`
 *   - paid        — Σ `amountForeign` of completed contributions
 *   - outstanding — planned − paid
 *   - overdueCount — `!complete && date < today`
 *   - upcomingCount — `!complete && date >= today`
 *
 * This is the contract-side view of the same money the `MoneyModelGrid`
 * summarizes above it — useful when invoices haven't all been issued yet but
 * the schedule already tells the financier what's coming. Money stages reuse
 * the shared `pages.groupDetail.money.*` keys (planned/paid/debt) so the term
 * for each stage is consistent across the tab (Req 5.4), and every color comes
 * from the shared `GroupColors` theme tokens (no hardcoded hex / inline color
 * literals — Req 11).
 */
export const ContributionsSummary = memo(
  ({ agreements }: IContributionsSummaryProps) => {
    const { t } = useTranslation();

    const rows: ICurrencyRow[] = useMemo(() => {
      const map = new Map<string, ICurrencyRow>();
      const today = moment().startOf('day');
      (agreements || []).forEach((a) => {
        if (!a || a.terminated) return;
        const cur = a.currency || '';
        const row = map.get(cur) || {
          currency: cur,
          planned: 0,
          paid: 0,
          outstanding: 0,
          overdueCount: 0,
          upcomingCount: 0,
        };
        (a.contributions?.edges || []).forEach((edge) => {
          const c = edge?.node;
          if (!c) return;
          const amount = Number(c.amountForeign) || 0;
          row.planned += amount;
          if (c.complete) {
            row.paid += amount;
          } else if (c.date && moment(c.date).isBefore(today)) {
            row.overdueCount += 1;
          } else {
            row.upcomingCount += 1;
          }
        });
        row.outstanding = Math.max(0, row.planned - row.paid);
        map.set(cur, row);
      });
      return Array.from(map.values()).sort((x, y) => y.planned - x.planned);
    }, [agreements]);

    if (rows.length === 0) {
      return (
        <S.Card>
          <S.CardTitle>
            {t('pages.groupDetail.finances.contributionsSection')}
          </S.CardTitle>
          <S.SubTitle>
            {t('pages.groupDetail.finances.noContributions')}
          </S.SubTitle>
        </S.Card>
      );
    }

    return (
      <S.Card>
        <S.CardTitle>
          {t('pages.groupDetail.finances.contributionsSection')}
        </S.CardTitle>
        <L.SmallTable>
          <thead>
            <tr>
              <th>{t('pages.groupDetail.finances.col.currency')}</th>
              <th>{t('pages.groupDetail.money.planned')}</th>
              <th>{t('pages.groupDetail.money.paid')}</th>
              <th>{t('pages.groupDetail.money.debt')}</th>
              <ColCenter>
                {t('pages.groupDetail.finances.col.overdue')}
              </ColCenter>
              <ColCenter>
                {t('pages.groupDetail.finances.col.upcoming')}
              </ColCenter>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.currency || 'unknown'}>
                <CurrencyTd>{r.currency || '—'}</CurrencyTd>
                <td>{formatMoneyCurrency(r.planned, r.currency)}</td>
                <PaidTd>{formatMoneyCurrency(r.paid, r.currency)}</PaidTd>
                <OutstandingTd positive={r.outstanding > 0}>
                  {formatMoneyCurrency(r.outstanding, r.currency)}
                </OutstandingTd>
                <OverdueTd active={r.overdueCount > 0}>
                  {r.overdueCount}
                </OverdueTd>
                <UpcomingTd>{r.upcomingCount}</UpcomingTd>
              </tr>
            ))}
          </tbody>
        </L.SmallTable>
      </S.Card>
    );
  },
);

const ColCenter = styled.th`
  text-align: center;
`;

const CurrencyTd = styled.td`
  font-weight: 600;
`;

const PaidTd = styled.td`
  color: ${GroupColors.success};
  font-weight: 600;
`;

const OutstandingTd = styled.td<{ positive: boolean }>`
  color: ${({ positive }) =>
    positive ? GroupColors.warn : GroupColors.textMuted};
  font-weight: ${({ positive }) => (positive ? 600 : 400)};
`;

const OverdueTd = styled.td<{ active: boolean }>`
  text-align: center;
  color: ${({ active }) =>
    active ? GroupColors.danger : GroupColors.textMuted};
  font-weight: ${({ active }) => (active ? 700 : 400)};
`;

const UpcomingTd = styled.td`
  text-align: center;
  color: ${GroupColors.textSecondary};
`;
